// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Observable, forkJoin, of, catchError, mergeMap, toArray, from, map, switchMap, throwError } from "rxjs";
import { HceReferenceService } from "./hce-reference/hce-reference.service";
import { HceAnalysisService } from "./hce-analysis/hce-analysis.service";
import { HceGender } from "../../interfaces/hce/hce-gender-prediction";
import { HceAlignmentData } from "../../interfaces/hce/hce-alignment-data";
import { HceRefGeneExonRaw, HceView } from "../../interfaces/hce/hce-reference";
import { HceEncryptionService } from "./hce-encryption/hce-encryption.service";
import { HceAlignmentViewRequest, HceAlignmentViewBody } from "../../interfaces/hce/hce-alignment-view";
import { HceAssembleViewRequest } from "../../interfaces/hce/hce-assemble-view";
import { HceApiService, FileMeta } from "./hce-api/hce-api.service";
import { HceFileManagementService } from "./hce-file-management/hce-file-management.service";
import { Analysis } from "../../interfaces/analysis";
import { ReportFile } from "../../components/hce/hce-reports-modal/hce-reports-modal.component";

@Injectable({
  providedIn: 'root'
})
export class HceService {
  public readonly getAnnotationFile: HceFileManagementService['getAnnotationFile'];

  constructor(
    private readonly api: HceApiService,
    private readonly file: HceFileManagementService,
    private readonly analysis: HceAnalysisService,
    private readonly reference: HceReferenceService,
    private readonly enc: HceEncryptionService,
  ) {
    this.getAnnotationFile = (...args) => this.file.getAnnotationFile(...args);
  }

  getAnalysis(analysisId: number): Observable<any> {
    return this.api.get(`/v1/analyses/${analysisId}?raw=true`);
  }

  getGender<T = HceGender>(analysisId: number): Observable<T | null> {
    return this.analysis.getMap2refGenderPredictionJson(analysisId).pipe(
      map(gp => gp?.gender ? gp.gender as T : null),
      catchError(() => of(null))
    );
  }

  getQcStats<T extends object = any>(analysisId: number): Observable<T> {
    return this.analysis.getQcStats0Json<T>(analysisId);
  }

  getAlignedAssemblyStats<T extends object = any>(analysisId: number): Observable<T> {
    return this.analysis.getSvAlignedAsmStatsJson<T>(analysisId);
  }

  getSvJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.analysis.getSvEnhancedHsvJson<T>(analysisId);
  }

  getEnhancedRefGene(analysesId: number): Observable<string> {
    return this.analysis.getSvEnhancedRefGene(analysesId);
  }
  
  getMultihits(analysisId: number): Observable<Record<string, Record<string, number[]>>> {
    return this.analysis.getSvEnhancedMultihitsJson(analysisId);
  }

  getSvEnhancedChr(analysis: Analysis, chromosomeId: number): Observable<HceAlignmentData> {
    return forkJoin({
      scaled: this.analysis.getSvEnhancedScaledJson(analysis, chromosomeId).pipe(
        map(value => ({ ok: true, value }) as const),
        catchError(error => of({ ok: false, error } as const)),
      ),
      gappedAlns: this.analysis.getSvEnhancedGappedAlnsJson(analysis, chromosomeId).pipe(
        map(value => ({ ok: true, value }) as const),
        catchError(error => of({ ok: false, error } as const)),
      ),
      contigs: this.analysis.getSvEnhancedContigsJson(analysis, chromosomeId).pipe(
        map(value => ({ ok: true, value }) as const),
        catchError(error => of({ ok: false, error } as const)),
      ),
    }).pipe(
      switchMap(({ scaled, gappedAlns, contigs }) => {
        if (scaled.ok) return of(scaled.value);
        if (gappedAlns.ok) return of(gappedAlns.value);
        if (contigs.ok) return of(contigs.value);
        const reasons = ([
          ['scaled', scaled],
          ['gappedAlns', gappedAlns],
          ['contigs', contigs],
        ] as const)
          .filter(([_, r]) => !r.ok)
          .map(([type, r]) => `${type}: ${r.error.message}`)
          .join(' | ');
        return throwError(() => new Error([
          `SV enhanced data file not found for chromosome ${chromosomeId}. `,
          `Reasons: ${reasons}`
        ].join(' ')));
      })
    )
  }

  getSvEnhancedChrs(analysis: Analysis): Observable<Record<number, HceAlignmentData>> {
    return forkJoin({
      scaled: this.analysis.getSvEnhancedScaledAll(analysis).pipe(
        map(value => ({ ok: true, value }) as const),
        catchError(error => of({ ok: false, error } as const)),
      ),
      gappedAlns: this.analysis.getSvEnhancedGappedAlnsAll(analysis).pipe(
        map(value => ({ ok: true, value }) as const),
        catchError(error => of({ ok: false, error } as const)),
      ),
      contigs: this.analysis.getSvEnhancedContigsAll(analysis).pipe(
        map(value => ({ ok: true, value }) as const),
        catchError(error => of({ ok: false, error } as const)),
      ),
    }).pipe(
      map(({ scaled, gappedAlns, contigs }) => {
        if (scaled.ok) return scaled.value;
        if (gappedAlns.ok) return gappedAlns.value;
        if (contigs.ok) return contigs.value;
        const reasons = ([
          ['scaled', scaled],
          ['gappedAlns', gappedAlns],
          ['contigs', contigs],
        ] as const)
          .filter(([_, r]) => !r.ok)
          .map(([type, r]) => `${type}: ${r.error.message}`)
          .join(' | ');
        throw new Error(`SV enhanced data file not found. Reasons: ${reasons}`);
      })
    );
  }

  getCoverage(analysisId: number): Observable<string | null> {
    return this.analysis.getSvEnhancedCoverage(analysisId);
  }

  getAssembleJson<T extends object = any>(analysisId: number, contigId: number): Observable<T | null> {
    return this.analysis.getAsmContigJson<T>(analysisId, contigId);
  }

  getReferenceMap(panelId: number): Observable<string> {
    return this.reference.getReferenceMap(panelId);
  }

  getView(panelId: number): Observable<HceView[]> {
    return this.reference.getViewTsv(panelId);
  }

  getRefGene(panelId: number): Observable<string> {
    return this.reference.getRefGene(panelId);
  }

  getRefGeneExonIntron(panelId: number): Observable<HceRefGeneExonRaw[]> {
    return this.reference.getRefGeneExonIntronTsv(panelId);
  }

  getRefsNameMapping(panelId: number) {
    return this.reference.getRefsNameMappingJson(panelId);
  }

  getUcscRefVersion(panelId: number): Observable<string> {
    return this.reference.getUcscRefVersion(panelId);
  }

  openUcscGenomeBrowser(db: string, chr: string, pos: number): void {
    const pad = 50_000;
    const start = Math.max(1, Math.round(pos - pad));
    const end = Math.max(start, Math.round(pos + pad));
    const url = new URL('https://genome.ucsc.edu/cgi-bin/hgTracks');
    url.searchParams.set('db', db);
    url.searchParams.set('position', `${chr}:${start}-${end}`);
    window.open(url.toString(), '_blank', 'noopener');
  }

  openAlignmentViewer(
    payload: HceAlignmentViewRequest,
    target: string,
    features: Record<string, number | boolean | string | null | undefined> = {
      width: 1300,
      height: 792,
      resizable: true,
      scrollbars: true,
      location: false,
      menubar: false,
      toolbar: false,
      status: false,
    },
  ): void {
    const text = JSON.stringify(payload)
    from(this.enc.encrypt(text)).subscribe({
      next: (id) => {
        const origin = window.location.origin;
        const url = new URL(`/analysis/hce/alignment/view/${id}`, origin);
        window.open(url, target, this.buildWindowFeatures(features));
      }
    });
  }

  mapAlignmentViewToBody(p: HceAlignmentViewRequest): HceAlignmentViewBody {
    return {
      job_id: String(p.analysisId),
      svid: typeof p.svId === 'number' ? String(p.svId) : p.svId,
      db: p.db,
      bp: typeof p.bp === 'number' ? String(p.bp) : p.bp,
      range: typeof p.range === 'number' ? String(p.range) : p.range,
      alignmentscale: p.alignmentScale,
      chromosomename: p.chromosomeName,
      annotation_file: p.annotationFile,
      contig: typeof p.contig === 'number' ? String(p.contig) : p.contig,
    };
  }

  mapAlignmentViewToRequest(p: HceAlignmentViewBody): HceAlignmentViewRequest {
    return {
      analysisId: p.job_id,
      svId: p.svid,
      db: p.db,
      bp: p.bp,
      range: p.range,
      alignmentScale: p.alignmentscale,
      chromosomeName: p.chromosomename,
      annotationFile: p.annotation_file,
      contig: p.contig,
    };
  }

  openAssembleViewer(
    payload: HceAssembleViewRequest,
    target: string,
    features: Record<string, number | boolean | string | null | undefined> = {
      width: 1300,
      height: 792,
      resizable: true,
      scrollbars: true,
      location: false,
      menubar: false,
      toolbar: false,
      status: false,
    },
  ): void {
    const text = JSON.stringify(payload)
    from(this.enc.encrypt(text)).subscribe({
      next: (id) => {
        const origin = window.location.origin;
        const url = new URL(`/analysis/hce/assemble/view/${id}`, origin);
        window.open(url, target, this.buildWindowFeatures(features));
      }
    });
  }

  buildWindowFeatures(
    features: Record<string, number | boolean | string | null | undefined>
  ): string {
    const toYesNo = (v: unknown) => v === true ? 'yes' : v === false ? 'no' : v;
    return Object.entries(features)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${k}=${toYesNo(v)}`)
      .join(',');
  }

  collectFormEntries(
    form: HTMLFormElement
  ): Record<string, FormDataEntryValue | FormDataEntryValue[]> {
    const out: Record<string, FormDataEntryValue | FormDataEntryValue[]> = {};
    const fd = new FormData(form);
    fd.forEach((value, key) => {
      if (key in out) {
        const prev = out[key];
        out[key] = Array.isArray(prev) ? [...prev, value] : [prev, value];
      } else {
        out[key] = value;
      }
    });
    return out;
  }

  getReportIncludeFlags(
    analysis: Analysis
  ): Observable<{
    includeAlignment: "scaled" | "gapped_alns" | "contigs" | undefined;
    includeUsedUnusedMaps: boolean;
    includeReadsMapsZip: boolean;
    includeMapReads: boolean;
    includeMapContigs: boolean;
  }> {
    const base = `${analysis.FolderName}/Results/Reports`;

    return forkJoin({
      enhancedScaled: this.file.existsFolder(
        `${base}/sv/results/enhanced.scaled`
      ),
      enhancedGappedAlns: this.file.existsFolder(
        `${base}/sv/results/enhanced.gapped_alns`
      ),
      enhancedContigs: this.file.existsFolder(
        `${base}/sv/results/enhanced.contigs`
      ),
      includeUsedUnusedMaps: this.file.existsFolder(
        `${base}/asm_results/output`
      ),
      includeReadsMapsZip: this.file.existsFolder(
        `${base}/input`
      ),
      includeMapReads: this.file.existsFolder(
        `${base}/input/reads`
      ),
      includeMapContigs: this.file.existsFolder(
        `${base}/input/contigs`
      ),
    }).pipe(
      map(flags => ({
        includeAlignment: (
          [
            [flags.enhancedScaled, "scaled"],
            [flags.enhancedGappedAlns, "gapped_alns"],
            [flags.enhancedContigs, "contigs"],
          ] as const
        ).find(([enabled]) => enabled)?.[1],
        includeUsedUnusedMaps: flags.includeUsedUnusedMaps,
        includeReadsMapsZip: flags.includeReadsMapsZip,
        includeMapReads: flags.includeMapReads,
        includeMapContigs: flags.includeMapContigs,
      }))
    );
  }

  getReportFilesIndex(
    analysis: Analysis,
    opts?: {
      includeRefs?: boolean;
      includeAlignment?: 'contigs' | 'gapped_alns' | 'scaled';
      includeUsedUnusedMaps?: boolean;
      includeReadsMapsZip?: boolean;
      includeMapContigs?: boolean;
      includeMapReads?: boolean;
    }
  ): Observable<ReportFile[]> {
    type Job = {
      label: string;
      meta$: Observable<FileMeta>;
      path?: string;
      refKey?: 'reference_map' | 'ref_gene' | 'ref_gene_exon_intron';
    };

    const analysisId = (analysis as any).AnalysisID;
    const panelId = (analysis as any).PanelID;

    const includeRefs = opts?.includeRefs ?? true;
    const includeAlignment = opts?.includeAlignment ?? "";
    const includeUsedUnused = opts?.includeUsedUnusedMaps ?? true;
    const includeReadsMapsZip = opts?.includeReadsMapsZip ?? true;
    const includeMapContigs = opts?.includeMapContigs ?? true;
    const includeMapReads = opts?.includeMapReads ?? true;

    const singleJobs: Job[] = [
      { label: 'SV JSON', meta$: this.analysis.getSvEnhancedHsvFileMeta(analysisId), path: 'sv/results/enhanced.hsv.json' },
      { label: 'Multihits JSON', meta$: this.analysis.getSvEnhancedMultihitsFileMeta(analysisId), path: 'sv/results/enhanced.multihits.json' },
      { label: 'Assembly Stats', meta$: this.analysis.getSvAlignedAsmStatsFileMeta(analysisId), path: 'sv/results/aligned_assembly_stats.json' },
      { label: 'SMRM Stats', meta$: this.analysis.getQcStats0FileMeta(analysisId), path: 'qc_results/stats0_results.json' },
      { label: 'Coverage Txt', meta$: this.analysis.getSvEnhancedCoverageFileMeta(analysisId), path: 'sv/results/enhanced.coverage.txt' },
      { label: 'Enhanced Ref.gene', meta$: this.analysis.getSvEnhancedRefGeneFileMeta(analysisId), path: 'sv/results/enhanced.ref_gene.txt' },
      { label: 'SV Params', meta$: this.analysis.getSvDetectionConfigFileMeta(analysisId), path: 'sv/results/sv_detection_config.json' },
      { label: 'Aligner Params(map2ref)', meta$: this.analysis.getMap2refGappedAlignerParamsFileMeta(analysisId), path: 'map2ref/config/gapped_aligner_params.json' },
      { label: 'Aligner Params(con2ref)', meta$: this.analysis.getSvGappedAlignerParamsFileMeta(analysisId), path: 'sv/results/config/gapped_aligner_params.json' },
      { label: 'Assembly Params', meta$: this.analysis.getAsmParamsFileMeta(analysisId), path: 'asm_results/output/config/assembly_params.json' },
    ];

    // ---------- used/unused 2D hist ----------
    const usedUnusedJobs: Job[] = includeUsedUnused ? [
      { label: 'Assembly reads-maps usage', meta$: this.analysis.getAsmUnusedInitMaps2dHistFileMeta(analysisId), path: 'asm_results/output/unused_init_maps_2d_hist.tsv' },
      { label: 'Assembly reads-maps usage', meta$: this.analysis.getAsmUnusedMaps2dHistFileMeta(analysisId), path: 'asm_results/output/unused_maps_2d_hist.tsv' },
      { label: 'Assembly reads-maps usage', meta$: this.analysis.getAsmUsedInitMaps2dHistFileMeta(analysisId), path: 'asm_results/output/used_init_maps_2d_hist.tsv' },
      { label: 'Assembly reads-maps usage', meta$: this.analysis.getAsmUsedMaps2dHistFileMeta(analysisId), path: 'asm_results/output/used_maps_2d_hist.tsv' },
    ] : [];

    // ---------- Nabsys zip ----------
    const nabsysJob: Job = {
      label: 'Nabsys format assembly files',
      meta$: this.analysis.getAsmNabsysFileMeta(analysisId),
      path: 'asm_results/output/assembly/nabsys.zip',
    };

    // ---------- refs/... ----------
    const refsJobs: Job[] = includeRefs ? [
      { label: 'Reference(SV)', meta$: this.reference.getReferenceMapFileMeta(panelId), refKey: 'reference_map' },
      { label: 'Reference Gene', meta$: this.reference.getRefGeneFileMeta(panelId), refKey: 'ref_gene' },
      { label: 'RefGene(exon)', meta$: this.reference.getRefGeneExonIntronFileMeta(panelId), refKey: 'ref_gene_exon_intron' },
    ] : [];

    // ---------- Alignment JSON ----------
    // ① enhanced.scaled
    const alignScaled$ = includeAlignment === "scaled"
      ? this.analysis.getSvEnhancedScaledFilesMeta(analysis).pipe(
        map(metas => metas.map(meta => ({
          fileType: 'Alignment JSON',
          fileName: meta.filename ?? '',
          sizeBytes: meta.size ?? 0,
          path: `sv/results/enhanced.scaled/${meta.filename ?? ''}`,
        } as ReportFile))),
        catchError(() => of<ReportFile[]>([]))
      )
      : of<ReportFile[]>([]);

    // ② enhanced.gapped_alns
    const alignGapped$ = includeAlignment === "gapped_alns"
      ? this.analysis.getSvEnhancedGappedAlnsFilesMeta(analysis).pipe(
        map(metas => metas.map(meta => ({
          fileType: 'Alignment JSON',
          fileName: meta.filename ?? '',
          sizeBytes: meta.size ?? 0,
          path: `sv/results/enhanced.gapped_alns/${meta.filename ?? ''}`,
        } as ReportFile))),
        catchError(() => of<ReportFile[]>([]))
      )
      : of<ReportFile[]>([]);

    // ③ enhanced.contigs
    const alignContigs$ = includeAlignment === "contigs"
      ? this.analysis.getSvEnhancedContigsFilesMeta(analysis).pipe(
        map(metas => metas.map(meta => ({
          fileType: 'Alignment JSON',
          fileName: meta.filename ?? '',
          sizeBytes: meta.size ?? 0,
          path: `sv/results/enhanced.contigs/${meta.filename ?? ''}`,
        } as ReportFile))),
        catchError(() => of<ReportFile[]>([]))
      )
      : of<ReportFile[]>([]);

    // ---------- Assemble JSON ----------
    const asmContigs$ = this.analysis.getAsmContigFilesMeta(analysis).pipe(
      map(metas => metas.map(meta => ({
        fileType: 'Assemble JSON',
        fileName: meta.filename ?? '',
        sizeBytes: meta.size ?? 0,
        path: `asm_results/output/assembly/gajson/contigs/${meta.filename ?? ''}`,
      } as ReportFile))),
      catchError(() => of<ReportFile[]>([]))
    );

    // ---------- Input ----------
    const readsMaps$ = includeReadsMapsZip
      ? this.analysis.getInputFilesMeta(analysis).pipe(
        map(metas =>
          metas
            .filter(m => (m.filename ?? '').toLowerCase().endsWith('.zip'))
            .map(meta => ({
              fileType: 'Input reads maps(zip)',
              fileName: meta.filename ?? '',
              sizeBytes: meta.size ?? 0,
              path: `input/${meta.filename ?? ''}`,
            } as ReportFile))
        ),
        catchError(() => of<ReportFile[]>([]))
      )
      : of<ReportFile[]>([]);

    // ---------- Map Rads & Map Contigs ----------
    const mapReads$ = includeMapReads 
      ? this.analysis.getInputReadsFilesMeta(analysis).pipe(
          map(metas =>
            metas
              .map(meta => ({
                fileType: 'Map Reads',
                fileName: meta.filename ?? '',
                sizeBytes: meta.size ?? 0,
                path: `input/reads/${meta.filename ?? ''}`,
              } as ReportFile))
          ),
          catchError(() => of<ReportFile[]>([]))
        ) 
      : of<ReportFile[]>([]);

    const mapContigs$ = includeMapContigs
      ? this.analysis.getInputContigsFilesMeta(analysis).pipe(
          map(metas =>
            metas
              .map(meta => ({
                fileType: 'Map Contigs',
                fileName: meta.filename ?? '',
                sizeBytes: meta.size ?? 0,
                path: `input/contigs/${meta.filename ?? ''}`,
              } as ReportFile))
          ),
          catchError(() => of<ReportFile[]>([]))
        )
      : of<ReportFile[]>([]);

    // ---------- refs & nabsys ----------
    const simple$ = from([
      ...singleJobs,
      ...usedUnusedJobs,
      nabsysJob,
      ...refsJobs,
    ] as Job[]).pipe(
      mergeMap(({ label, meta$, path, refKey }: Job) =>
        meta$.pipe(
          map(meta => {
            const resolvedPath = path ?? (refKey ? `refs/${meta.filename ?? ''}` : undefined);
            return {
              fileType: label,
              fileName: meta.filename ?? '',
              sizeBytes: meta.size ?? 0,
              path: resolvedPath,
            } as ReportFile;
          }),
          catchError(() => of<ReportFile | null>(null))
        ),
        8
      ),
      toArray(),
      map(list => list.filter((x): x is ReportFile => !!x))
    );

    // ---------- merge all ----------
    return forkJoin([simple$, alignScaled$, alignGapped$, alignContigs$, asmContigs$, readsMaps$, mapContigs$, mapReads$]).pipe(
      map(([simple, a1, a2, a3, asm, input, mapContigs, mapReads]) => [...simple, ...a1, ...a2, ...a3, ...asm, ...input, ...mapContigs, ...mapReads]),
      map(list => {
        list.sort((a, b) => {
          const t = a.fileType.localeCompare(b.fileType, undefined, { sensitivity: 'base' });
          return t !== 0 ? t : a.fileName.localeCompare(b.fileName, undefined, { numeric: true, sensitivity: 'base' });
        });
        return list;
      })
    );
  }
}
